import { getAnthropicApiKey } from '../storage';
import { parseRetryAfter } from '../http-retry';
import { AnthropicApiError } from './types';
import type {
  AnthropicUsageResponse,
  AnthropicCostResponse,
  AnthropicApiKey,
  AnthropicApiKeysResponse,
  AnthropicWorkspacesResponse,
  AnthropicUsersResponse,
  AnthropicApiKeysParams,
  UsageReportParams,
  CostReportParams
} from './types';
import {
  ANTHROPIC_API_BASE,
  ANTHROPIC_VERSION,
  ANTHROPIC_FAST_MODE_BETA,
  MIN_REQUEST_INTERVAL
} from './constants';
import { debugLog } from '../debug-log';


/**
 * Sleep helper for rate limiting
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// Global rate limiting state
let lastRequestTime = 0;

const buildAnthropicProxyHeaders = (apiKey: string, beta?: string): HeadersInit => {
  const headers: Record<string, string> = {
    'x-anthropic-api-key': apiKey,
    'anthropic-version': ANTHROPIC_VERSION,
    'User-Agent': 'Usage-Dashboard/1.0.0'
  };
  if (beta) {
    headers['anthropic-beta'] = beta;
  }
  return headers;
};

const buildAnthropicUrl = (path: string): URL => {
  const base = ANTHROPIC_API_BASE.endsWith('/')
    ? ANTHROPIC_API_BASE.slice(0, -1)
    : ANTHROPIC_API_BASE;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const target = `${base}${normalizedPath}`;

  if (/^https?:\/\//i.test(target)) {
    return new URL(target);
  }

  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  return new URL(target, origin);
};

function getHttpStatusMessage(status: number, statusText: string): string {
  switch (status) {
    case 502: return 'Bad Gateway — the upstream server returned an invalid response';
    case 503: return 'Service Unavailable — the server is temporarily overloaded';
    case 504: return 'Gateway Timeout — the upstream server did not respond in time';
    case 524: return 'Server Timeout — the origin server took too long to respond (Cloudflare)';
    default:  return statusText || `HTTP error ${status}`;
  }
}

export const throwAnthropicApiError = async (response: Response): Promise<never> => {
  const retryAfterHeader = response.headers.get('retry-after');
  const retryAfterMs = parseRetryAfter(retryAfterHeader);

  const errorText = await response.text();
  let upstreamMessage = errorText;

  try {
    const parsed = JSON.parse(errorText);
    const candidate = parsed?.error?.message;
    if (typeof candidate === 'string' && candidate.trim()) {
      upstreamMessage = candidate.trim();
    }
  } catch {
    // HTMLレスポンス（Cloudflare エラーページ等）の場合はサニタイズ
    if (errorText.includes('<!DOCTYPE') || errorText.includes('<html')) {
      upstreamMessage = getHttpStatusMessage(response.status, response.statusText);
    }
    // 短いプレーンテキストはそのまま保持
  }

  if (response.status === 401 && /invalid x-api-key/i.test(upstreamMessage)) {
    throw new AnthropicApiError('Anthropic Admin API key is invalid. Re-save a valid Admin key in Settings.', response.status, null);
  }

  if (response.status === 403) {
    throw new AnthropicApiError(`Anthropic API permission denied (403): ${upstreamMessage}`, response.status, null);
  }

  throw new AnthropicApiError(`Anthropic API error: ${response.status} - ${upstreamMessage}`, response.status, retryAfterMs);
};

/**
 * Fetch usage report from Anthropic API
 */
export async function fetchAnthropicUsageReport(
  params: UsageReportParams
): Promise<AnthropicUsageResponse> {
  const apiKey = getAnthropicApiKey();
  if (!apiKey) {
    throw new Error('Anthropic Admin API key is not configured');
  }

  // Enforce minimum interval between requests
  const now = Date.now();
  const timeSinceLastRequest = now - lastRequestTime;
  if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
    await sleep(MIN_REQUEST_INTERVAL - timeSinceLastRequest);
  }
  lastRequestTime = Date.now();

  const url = buildAnthropicUrl('/organizations/usage_report/messages');

  // Build query parameters
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined) return;

    if (Array.isArray(value)) {
      value.forEach(v => url.searchParams.append(`${key}[]`, v));
    } else {
      url.searchParams.append(key, value.toString());
    }
  });

  debugLog('[Anthropic Usage] Request', {
    endpoint: url.pathname,
    query: url.search,
    bucketWidth: params.bucket_width,
    starting_at: params.starting_at,
    ending_at: params.ending_at,
    page: params.page,
    limit: params.limit
  });

  const needsFastModeBeta = params.group_by?.includes('speed') || Boolean(params.speeds?.length);
  const headers = buildAnthropicProxyHeaders(
    apiKey,
    needsFastModeBeta ? ANTHROPIC_FAST_MODE_BETA : undefined
  );

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers
  });

  if (!response.ok) {
    await throwAnthropicApiError(response);
  }

  return response.json();
}

/**
 * Fetch cost report from Anthropic API
 */
export async function fetchAnthropicCostReport(
  params: CostReportParams
): Promise<AnthropicCostResponse> {
  const apiKey = getAnthropicApiKey();
  if (!apiKey) {
    throw new Error('Anthropic Admin API key is not configured');
  }

  const url = buildAnthropicUrl('/organizations/cost_report');

  // Build query parameters
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined) return;

    if (Array.isArray(value)) {
      value.forEach(v => url.searchParams.append(`${key}[]`, v));
    } else {
      url.searchParams.append(key, value.toString());
    }
  });

  const headers = buildAnthropicProxyHeaders(apiKey);

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers
  });

  if (!response.ok) {
    await throwAnthropicApiError(response);
  }

  return response.json();
}

/**
 * Fetch single API key metadata from Anthropic API
 */
export async function fetchAnthropicApiKey(
  apiKeyId: string,
  attempt = 0
): Promise<AnthropicApiKey> {
  const apiKey = getAnthropicApiKey();
  if (!apiKey) {
    throw new Error('Anthropic Admin API key is not configured');
  }

  const url = buildAnthropicUrl(`/organizations/api_keys/${apiKeyId}`);

  const headers = buildAnthropicProxyHeaders(apiKey);

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers
  });

  if (!response.ok) {
    if (response.status === 429 && attempt < 4) {
      const backoff = 2000 * Math.pow(2, attempt) + Math.floor(Math.random() * 1000);
      console.warn(`Anthropic API key metadata rate limited. Retrying ${apiKeyId} in ${backoff}ms...`);
      await sleep(backoff);
      return fetchAnthropicApiKey(apiKeyId, attempt + 1);
    }
    await throwAnthropicApiError(response);
  }

  const payload = await response.json();
  if (payload?.data && typeof payload.data === 'object') {
    return payload.data as AnthropicApiKey;
  }
  if (payload?.api_key && typeof payload.api_key === 'object') {
    return payload.api_key as AnthropicApiKey;
  }
  return payload as AnthropicApiKey;
}

/**
 * Fetch API keys metadata from Anthropic API
 */
export async function fetchAnthropicApiKeys(
  params: AnthropicApiKeysParams = {},
  attempt = 0
): Promise<AnthropicApiKeysResponse> {
  const apiKey = getAnthropicApiKey();
  if (!apiKey) {
    throw new Error('Anthropic Admin API key is not configured');
  }

  const url = buildAnthropicUrl('/organizations/api_keys');

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    url.searchParams.append(key, value.toString());
  });

  const headers = buildAnthropicProxyHeaders(apiKey);

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers,
    signal: AbortSignal.timeout(30_000)
  });

  if (!response.ok) {
    if (response.status === 429 && attempt < 4) {
      const backoff = 2000 * Math.pow(2, attempt) + Math.floor(Math.random() * 1000);
      console.warn(`Anthropic API key list rate limited. Retrying in ${backoff}ms...`);
      await sleep(backoff);
      return fetchAnthropicApiKeys(params, attempt + 1);
    }
    await throwAnthropicApiError(response);
  }

  return response.json();
}

export async function fetchAnthropicWorkspaces(
  params: { include_archived?: boolean; limit?: number; after_id?: string } = {},
  attempt = 0
): Promise<AnthropicWorkspacesResponse> {
  const apiKey = getAnthropicApiKey();
  if (!apiKey) {
    throw new Error('Anthropic Admin API key is not configured');
  }

  const url = buildAnthropicUrl('/organizations/workspaces');

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    url.searchParams.append(key, value.toString());
  });

  const headers = buildAnthropicProxyHeaders(apiKey);

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers,
    signal: AbortSignal.timeout(30_000)
  });

  if (!response.ok) {
    if (response.status === 429 && attempt < 4) {
      const backoff = 2000 * Math.pow(2, attempt) + Math.floor(Math.random() * 1000);
      console.warn(`Anthropic workspace list rate limited. Retrying in ${backoff}ms...`);
      await sleep(backoff);
      return fetchAnthropicWorkspaces(params, attempt + 1);
    }
    await throwAnthropicApiError(response);
  }

  return response.json();
}

export async function fetchAnthropicUsers(
  params: { limit?: number; after_id?: string } = {},
  attempt = 0
): Promise<AnthropicUsersResponse> {
  const apiKey = getAnthropicApiKey();
  if (!apiKey) {
    throw new Error('Anthropic Admin API key is not configured');
  }

  const url = buildAnthropicUrl('/organizations/users');

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    url.searchParams.append(key, value.toString());
  });

  const headers = buildAnthropicProxyHeaders(apiKey);

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers,
    signal: AbortSignal.timeout(30_000)
  });

  if (!response.ok) {
    if (response.status === 429 && attempt < 4) {
      const backoff = 2000 * Math.pow(2, attempt) + Math.floor(Math.random() * 1000);
      console.warn(`Anthropic users list rate limited. Retrying in ${backoff}ms...`);
      await sleep(backoff);
      return fetchAnthropicUsers(params, attempt + 1);
    }
    await throwAnthropicApiError(response);
  }

  return response.json();
}

/**
 * Determine appropriate bucket width based on date range
 */
export function determineAnthropicBucketWidth(startDate: Date, endDate: Date): '1m' | '1h' | '1d' {
  const diffMs = endDate.getTime() - startDate.getTime();
  const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;

  if (diffMs > ONE_DAY_IN_MS) {
    return '1d';
  }

  return '1h';
}
